import {
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { environment } from '../../../../../../../environments/environment.local';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  GenericTableComponent,
  ColumnConfig,
  FieldType,
  GlobalAction,
  ModalFieldConfig,
  ModalMode,
  ModalTabConfig,
  TableAction,
  TableActionConfig,
  GlobalApiConfigService,
  HeaderConfigurationService,
  ModelApiService,
  ButtonType,
  ButtonConfig,
} from '@solcre-org/core-ui';
import { Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '@solcre-org/core';
import { ContactModel } from '../../../../../../shared/models/contact.model';
import { ClientModel } from '../../../../../../shared/models/client.model';

@Component({
  selector: 'app-contact',
  imports: [GenericTableComponent, TranslateModule],
  templateUrl: './contact.html',
})
export class Contact implements OnInit, OnDestroy {
  private translateService: TranslateService = inject(TranslateService);
  private route = inject(ActivatedRoute);
  private apiService = inject(ApiService);
  private hasUserInteracted = signal<boolean>(false);
  private globalApiConfigService = inject(GlobalApiConfigService);
  private headerConfigurationService = inject(HeaderConfigurationService);
  private modelApiService = inject(ModelApiService);
  private router = inject(Router);

  fieldErrors = signal<Map<string, string[]>>(new Map());
  showValidationErrors = signal<boolean>(false);
  modalMode = signal<ModalMode>(ModalMode.EDIT);
  editedData = signal<any | null>(null);
  clientId = signal<string | null>(null);
  contactsData = signal<ContactModel[]>([]);
  contact = signal<ContactModel[]>([]);
  endpoint = computed<string>(() => {
    return environment.API.CLIENTS_CONTACTS_URI.replace(
      ':id',
      this.clientId() || ''
    );
  });
  customParams = computed<Record<string, any>>(() => {
    return {
      client: this.clientId(),
    };
  });
  columns: ColumnConfig<ContactModel>[] = [
    {
      label: 'contact.name',
      key: 'name',
    },
    {
      label: 'contact.email',
      key: 'email',
    },
    {
      label: 'contact.phone',
      key: 'phone',
    },
    {
      label: 'contact.position',
      key: 'position',
    },
    {
      label: 'contact.isPrimary',
      key: 'isPrimary',
      format: (value: boolean) => (value ? 'Sí' : 'No'),
    },
    {
      label: 'contact.isActive',
      key: 'isActive',
      format: (value: boolean) => (value ? 'Activo' : 'Inactivo'),
    },
  ];

  modalFieldsTabs = computed<ModalTabConfig<ContactModel>[]>(
    () =>
      [
        {
          tabId: 'general_data',
          tabLabel: 'contact.general_data',
          fields: [
            {
              label: 'contact.client',
              placeholder: 'contact.clientPlaceholder',
              key: 'client',
              defaultValue: '/api/clients/' + this.clientId(),
              type: FieldType.TEXT,
              readonly: false,
              visible: false,
            },
            {
              label: 'contact.name',
              placeholder: 'contact.namePlaceholder',
              key: 'name',

              type: FieldType.TEXT,
              readonly: false,
              validators: [Validators.required],
              errorMessages: {
                required: this.translateService.instant('validators.required'),
              },
            },
            {
              label: 'contact.email',
              placeholder: 'contact.emailPlaceholder',
              key: 'email',
              type: FieldType.TEXT,
              readonly: false,
              validators: [Validators.required, Validators.email],
              errorMessages: {
                required: this.translateService.instant('validators.required'),
                email: this.translateService.instant('validators.email'),
              },
            },
            {
              label: 'contact.phone',
              placeholder: 'contact.phonePlaceholder',
              key: 'phone',
              type: FieldType.TEXT,
              readonly: false,
              validators: [Validators.required],
              errorMessages: {
                required: this.translateService.instant('validators.required'),
              },
            },
            {
              label: 'contact.position',
              placeholder: 'contact.positionPlaceholder',
              key: 'position',
              type: FieldType.TEXT,
              readonly: false,
            },
            {
              label: 'contact.isPrimary',
              key: 'isPrimary',
              type: FieldType.SWITCH,
              readonly: false,
            },
            {
              label: 'contact.isActive',
              key: 'isActive',
              type: FieldType.SWITCH,
              defaultValue: true,
              readonly: false,
            },
          ],
        },
      ] as any as ModalTabConfig<ContactModel>[]
  );

  actions: TableActionConfig[] = [
    { action: TableAction.EDIT, isExtra: false },
    { action: TableAction.DELETE, isExtra: true },
    { action: TableAction.CREATE, isExtra: false },
  ];

  ngOnInit() {
    this.clientId.set(this.route.snapshot.paramMap.get('id'));
    this.globalApiConfigService.configure({
      apiUrl: environment.API_URL,
      updateWithPatch: true,
    });
    // this.loadContactsData();

    this.modelApiService.setContext('contacts');
    this.headerConfigurationService.setEndpointHeaders(this.endpoint(), {
      patch: [{ 'Content-Type': 'application/merge-patch+json' }],
    });
  }

  // private loadContactsData(): void {
  //   const endpoint = this.endpoint();
  //   if (!endpoint) return;

  //   this.apiService.fetchData(endpoint).subscribe({
  //     next: (response: any) => {
  //       this.contactsData.set(response);
  //     },
  //     error: (error) => {
  //       this.contactsData.set([]);
  //     }
  //   });
  // }

  get generalFields(): ModalFieldConfig<any>[] {
    return [];
  }

  get contactFields(): ModalFieldConfig<any>[] {
    return [
      {
        key: 'name',
        label: 'onboarding.company-data.contact.phone',
        type: FieldType.TEXT,
        readonly: false,
        validators: [Validators.required],
        errorMessages: {
          required: 'onboarding.company-data.contact.phone_required',
        },
      },
      {
        key: 'personal_contact',
        label: 'onboarding.company-data.contact.personal_contact',
        type: FieldType.TEXT,
        readonly: false,
        validators: [Validators.required],
        errorMessages: {
          required: 'onboarding.company-data.contact.personal_contact_required',
        },
      },
      {
        key: 'business_hours',
        label: 'onboarding.company-data.contact.business_hours',
        type: FieldType.TEXT,
        readonly: false,
        validators: [Validators.required],
        errorMessages: {
          required: 'onboarding.company-data.contact.business_hours_required',
        },
      },
      {
        key: 'address',
        label: 'onboarding.company-data.contact.address',
        type: FieldType.TEXTAREA,
        readonly: false,
        validators: [Validators.required],
        errorMessages: {
          required: 'onboarding.company-data.contact.address_required',
        },
      },
      {
        key: 'holidays',
        label: 'onboarding.company-data.contact.holidays',
        type: FieldType.TEXT,
        readonly: false,
        validators: [Validators.required],
        errorMessages: {
          required: 'onboarding.company-data.contact.holidays_required',
        },
      },
    ];
  }

  onFieldValueChange(key: string, value: any): void {
    this.hasUserInteracted.set(true);
  }

  getFieldErrors(key: string): string[] {
    if (!this.showValidationErrors()) {
      return [];
    }
    return this.fieldErrors().get(key) || [];
  }

  contactsModelFactory = (data: any): ContactModel => {
    return new ContactModel().fromJSON(data);
  };

  ngOnDestroy(): void {
    this.globalApiConfigService.configure({
      apiUrl: environment.API_URL,
      updateWithPatch: false,
    });
  }

  globalCustomActions = computed<GlobalAction<ContactModel>[]>(() => [
    {
      action: 'goBackToClients',
      label: this.translateService.instant('clients.back_to_clients'),
      icon: 'icon-arrow-left',
      callback: () => this.goBackToClients(),
      class: 'c-btn c-btn--stroked',
    }
  ]);

  createButtonConfig = signal<Partial<ButtonConfig>>({
    text: this.translateService.instant('contact.addContact'),
    type: ButtonType.PRIMARY
  });

  goBackToClients(): void {
    this.router.navigate(['/app/clients']);
  }
}
